import {
    TwilioConversation,
    ConversationUpdateReason,
    Message,
    MessageUpdateReason,
    ConversationParticipant,
    ConversationParticipantUpdateReason,
    PushNotification,
    ConversationsUser,
    UserUpdateReason,
    ConnectionError,
    TwilsockConnectionState
} from "~/modules/conversations";
import { Action } from "~/modules/actions/Action";
import { ContextManager, ContextInstance } from "~/modules/contextManager/ContextManager";
import { getLogger } from "~/modules/logger/LoggerFactory/getLogger";
import { LoggerName } from "~/modules/logger/Logger/LoggerName";
import { ConversationsSdkImpl } from "~/modules/conversations/ConversationsSdkImpl";
import { Conversation, ConversationImpl } from "~/modules/Conversation";
import { sendWorkerTrackingEvent } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { throwFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";




export interface ConversationClientEvents {
    conversationAdded: (conversation: Conversation) => void;
    conversationJoined: (conversation: Conversation) => void;
    conversationLeft: (conversation: Conversation) => void;
    conversationRemoved: (conversation: Conversation) => void;
    conversationUpdated: (data: { conversation: Conversation; updateReasons: ConversationUpdateReason[] }) => void;
    participantJoined: (participant: ConversationParticipant) => void;
    participantLeft: (participant: ConversationParticipant) => void;
    participantUpdated: (data: {
        participant: ConversationParticipant;
        updateReasons: ConversationParticipantUpdateReason[];
    }) => void;
    messageAdded: (message: Message) => void;
    messageRemoved: (message: Message) => void;
    messageUpdated: (data: { message: Message; updateReasons: MessageUpdateReason[] }) => void;
    tokenAboutToExpire: () => void;
    tokenExpired: () => void;
    typingEnded: (participant: ConversationParticipant) => void;
    typingStarted: (participant: ConversationParticipant) => void;
    pushNotification: (pushNotification: PushNotification) => void;
    userSubscribed: (user: ConversationsUser) => void;
    userUnsubscribed: (user: ConversationsUser) => void;
    userUpdated: (data: { user: ConversationsUser; updateReasons: UserUpdateReason[] }) => void;
    initialized: () => void;
    initFailed: (data: { error?: ConnectionError }) => void;
    connectionStateChanged: (state: TwilsockConnectionState) => void;
    connectionError: (data: ConnectionError) => void;
}



export interface AddConversationEventListenerResponse {
    


    unsubscribe: () => void;
}

































export class AddConversationEventListener<T extends keyof ConversationClientEvents>
    implements Action<Promise<AddConversationEventListenerResponse>>
{
    readonly #eventName: T;

    readonly #listener: ConversationClientEvents[T];

    constructor(eventName: T, listener: ConversationClientEvents[T]) {
        this.#eventName = eventName;
        this.#listener = listener;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const conversationSdk = context.retrieveInstanceOf(ContextInstance.ConversationsSdkImpl, ConversationsSdkImpl);
        const logger = getLogger(context)(LoggerName.Client);
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const worker = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl).worker;

        try {
            await conversationSdk.init();
        } catch (error) {
            const errorMsg = `AddConversationEventListener: Failed to initialize Conversations SDK`;
            logger.error(errorMsg, error);
            sendWorkerTrackingEvent(EVENTS.AddConversationEventListenerCompleted, analytics, worker, {
                error: errorMsg
            });
            throwFlexSdkError(context)(
                ErrorCode.FailedToInitializeConversationsSDK,
                { severity: ErrorSeverity.Error },
                errorMsg,
                error
            );
        }
        const handler = this.#createEventHandler(context, this.#eventName);
        if (!handler) {
            const errorMsg = `AddConversationEventListener: Failed to add conversation event listener for event '${
                this.#eventName
            }'`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.AddConversationEventListenerCompleted, analytics, worker, {
                error: errorMsg
            });
            return throwFlexSdkError(context)(
                ErrorCode.FailedToAddConversationEventListener,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }

        conversationSdk.conversationsClient.on(this.#eventName, handler);

        return {
            unsubscribe: () => {
                conversationSdk.conversationsClient.off(this.#eventName, handler);
                logger.debug(`AddConversationEventListener: Unsubscribed from '${this.#eventName}'`);
            }
        };
    }

    #createEventHandler(context: ContextManager, eventName: T): ((...args: unknown[]) => void) | undefined {
        switch (eventName) {
            case "conversationUpdated":
                return (data: { conversation: TwilioConversation; updateReasons: ConversationUpdateReason[] }) => {
                    const conversation = new ConversationImpl(context, data.conversation);
                    const eventData = {
                        conversation,
                        updateReasons: data.updateReasons
                    };
                    (this.#listener as ConversationClientEvents["conversationUpdated"])(eventData);
                };
            case "conversationAdded":
            case "conversationJoined":
            case "conversationLeft":
            case "conversationRemoved":
                return (addedConversation: TwilioConversation) => {
                    const conversation = new ConversationImpl(context, addedConversation);
                    (this.#listener as ConversationClientEvents["conversationAdded"])(conversation);
                };
            case "messageAdded":
            case "messageUpdated":
            case "messageRemoved":
            case "participantJoined":
            case "participantLeft":
            case "participantUpdated":
            case "connectionError":
            case "connectionStateChanged":
            case "initFailed":
            case "initialized":
            case "pushNotification":
            case "tokenAboutToExpire":
            case "tokenExpired":
            case "typingEnded":
            case "typingStarted":
            case "userSubscribed":
            case "userUnsubscribed":
            case "userUpdated":
                return (...args: unknown[]) => {
                    (this.#listener as (...args: unknown[]) => void)(...args);
                };
            default:
                return undefined;
        }
    }
}
